import {
  useIM,
  NotificationIncomingEvent,
  HistoryMessageItem,
  ReactionAddedByOtherEvent,
  ReactionRemovedByOtherEvent,
  ConferenceMemberRoleChangedByOtherEvent,
  MessageStatusType,
  MessageDeletedByOtherEvent,
  isMessageDeleted,
  MessageOutgoingEvent,
  MessageIncomingEvent,
  AnyConferenceChangedByOtherEvent,
} from '@wui/im'
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'

import { useStoreContacts } from '@/entities/contact'
import { useChatMessageStore } from '@/entities/messages'
import { fetchMessage } from '@/entities/not-in-history-messages'
import { useStoreLastReadEventIds, useStoreRooms } from '@/entities/room'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { Contact } from '@/shared/types/api'
import {
  AsideNotificationItem,
  AsideNotificationType,
  AsideNotificationEvent,
} from '@/shared/types/store'
import { NOTIFICATION_CONTACT_URI } from '@/shared/utils/constants'
import { insertAtStore } from '@/shared/utils/insert-at-store'
import { sanitizeToText } from '@/shared/utils/sanitize'
import { fromPServedUser, pServedIsRoom } from '@/shared/utils/uri'

export const useNotificationItemStore = defineStore('notification-items', () => {
  const { t } = useLocalization()
  const { requestChatHistory, onNotification, onDeleteMessage, onConferenceEdited } = useIM()
  const { getRoomById } = useStoreRooms()
  const { getMessageEventIdByRoomAndContactId } = useStoreLastReadEventIds()
  const { currentUser } = useCurrentUser()
  const storeContacts = useStoreContacts()

  const notifications = ref<Array<AsideNotificationItem>>([])

  const unreadCount = computed(() => {
    return notifications.value.filter((notification: AsideNotificationItem) => notification.unread)?.length || 0
  })

  const addNotification = (roomId: string, event: AsideNotificationEvent, type: AsideNotificationType) => {
    const pServedUser = event.pServedUser
    if (!event.eventId || !pServedUser) return

    if (!currentUser.value?.imLogin) {
      return
    }

    const from = event.sender
    const targetEventId = event.targetEventId
    if (!from || !targetEventId) {
      return
    }
    const contact = currentUser.value.imLogin === from ? currentUser.value : storeContacts.findByPServed(from)
    const { t } = useLocalization()
    const contactName = contact?.name || t('common.unknown')

    let unread = false
    if (
      type === 'Mention' ||
      type === 'AddToChat' ||
      type === 'AddReaction' ||
      type === 'ChangeRoleToModerator' ||
      type === 'ChangeRoleToUser'
    ) {
      const id = pServedIsRoom(roomId) ? fromPServedUser(roomId) : roomId
      const room = getRoomById(id)
      unread = true
      const alreadyExistNotification = notifications.value.find(
        (notification: AsideNotificationItem) => notification.roomId === roomId && notification.eventId === event.eventId,
      )

      if (!room) {
        unread = false
      }
      // Для реакций всегда помечаем как непрочитанные (независимо от targetEventId)
      const myLastReadEventId = getMessageEventIdByRoomAndContactId(pServedUser, currentUser.value.imLogin)
      if (myLastReadEventId && type !== 'AddReaction') {
        unread = targetEventId > myLastReadEventId
      }
      if (alreadyExistNotification) {
        unread = alreadyExistNotification.unread
      }
    }
    if (from === currentUser.value?.imLogin) {
      return
    }
    const message: AsideNotificationItem = {
      text: sanitizeToText(event.message),
      description: contactName,
      timestamp: event.timestamp,
      eventId: event.eventId,
      unread,
      targetEventId,
      roomId,
      pServedUser,
      contact: contact as Contact,
      type,
      miniature: event.miniature!,
      from,
    }
    insertAtStore(notifications.value, message, 'eventId')
  }

  const updateUnread = (roomPServedUser: string, lastReadEventId: number) => {
    notifications.value
      .filter((notification: AsideNotificationItem) => {
        // Для реакций не помечаем как прочитанные при чтении сообщения
        // Они помечаются как прочитанные только при видимости сообщения с реакцией
        if (notification.type === 'AddReaction') {
          return false
        }
        return notification.pServedUser === roomPServedUser && notification.targetEventId <= lastReadEventId
      })
      .forEach((notification: AsideNotificationItem) => {
        notification.unread = false
      })
  }

  const handleNotificationMessage = async (message: HistoryMessageItem<NotificationIncomingEvent>) => {
    const { eventId, timestamp, item: { type, sender, sourceRemoteId, notificationEventId }} = message
    if (type === 'IncomingMessageEvent') {
      const targetEvent = await fetchMessage<MessageIncomingEvent | MessageOutgoingEvent>(notificationEventId, sourceRemoteId)

      if (targetEvent && isMessageDeleted(targetEvent)) {
        return
      }

      addNotification(sourceRemoteId, {
        pServedUser: sourceRemoteId,
        eventId,
        sender,
        targetEventId: notificationEventId,
        message: t('common.youAreMentioned'),
        timestamp,
      }, 'Mention')
    } else if (type === 'ReactionAddedEvent') {
      const { reactionType } = message.item as ReactionAddedByOtherEvent
      addNotification(sourceRemoteId, {
        pServedUser: sourceRemoteId,
        eventId,
        sender,
        targetEventId: (message.item as ReactionAddedByOtherEvent).targetEventId,
        message: t('common.reactionOnYourMessage'),
        timestamp,
        miniature: reactionType,
      }, 'AddReaction')
    } else if (type === 'ReactionRemovedEvent') {
      const reactionRemovedItem = message.item as ReactionRemovedByOtherEvent
      const { reactionType, targetEventId: messageTargetEventId } = reactionRemovedItem
      addNotification(sourceRemoteId, {
        pServedUser: sourceRemoteId,
        eventId,
        sender,
        targetEventId: notificationEventId,
        message: t('common.reactionHasBeenRemoved'),
        timestamp,
        miniature: reactionType,
      }, 'RemoveReaction')
      // Нотификацию о добавлении реакции помечаем прочитанной — реакцию удалили
      markReactionNotificationAsRead(messageTargetEventId, sourceRemoteId)
    } else if (type === 'ConferenceMemberRoleChangedEvent') {
      const { targetRole } = message.item as ConferenceMemberRoleChangedByOtherEvent
      const isUser = targetRole === 'User'
      addNotification(sourceRemoteId, {
        pServedUser: sourceRemoteId,
        eventId,
        sender,
        targetEventId: eventId,
        message: isUser
          ? t('common.youAreNotModerator')
          : t('common.youAreModerator'),
        timestamp,
      }, isUser ? 'ChangeRoleToUser' : 'ChangeRoleToModerator')
    } else if (type === 'ConferenceMemberAddedEvent') {
      const messageText = sender === currentUser.value?.imLogin ? t('common.youHaveJoinedToChat') : t('common.youHasBeenAddedToChat')
      addNotification(sourceRemoteId, {
        pServedUser: sourceRemoteId,
        eventId,
        sender,
        targetEventId: eventId,
        message: messageText,
        timestamp,
      }, 'AddToChat')
    } else if (type === 'ConferenceMemberRemoved') {
      const messageText = sender === currentUser.value?.imLogin ? t('common.youHaveLeftChat') : t('common.youHasBeenRemovedFromChat')
      addNotification(sourceRemoteId, {
        pServedUser: sourceRemoteId,
        eventId,
        sender,
        targetEventId: eventId,
        message: messageText,
        timestamp,
      }, 'RemoveFromChat')
    } else if (type === 'ConferenceCreated') {
      addNotification(sourceRemoteId, {
        pServedUser: sourceRemoteId,
        eventId,
        sender,
        targetEventId: eventId,
        message: t('common.youHasBeenAddedToChat'),
        timestamp,
      }, 'AddToChat')
    }
  }

  const removeNotificationByTargetEventId = (targetEventId: number) => {
    const index = notifications.value.findIndex((item: AsideNotificationItem) => item.targetEventId === targetEventId)
    if (index !== -1) {
      notifications.value.splice(index, 1)
    }
  }

  const markReactionNotificationAsRead = (targetEventId: number, pServedUser: string) => {
    const notification = notifications.value.find(
      (item: AsideNotificationItem) =>
        item.type === 'AddReaction' &&
        item.targetEventId === targetEventId &&
        item.pServedUser === pServedUser &&
        item.unread,
    )

    if (!notification) {
      return
    }

    notification.unread = false
  }

  const { chatStoreIsReady } = useChatMessageStore()

  chatStoreIsReady.then((isReady) => {
    if (!isReady) {
      return
    }
    requestChatHistory({
      pServed: NOTIFICATION_CONTACT_URI,
      messagesLimit: 100,
    }).then((history) => {
      history.events.forEach((event: HistoryMessageItem) => {
        handleNotificationMessage(event as HistoryMessageItem<NotificationIncomingEvent>)
      })
    })
  })

  const { allContacts } = storeToRefs(storeContacts)
  watch(() => allContacts.value.length, () => {
    notifications.value.filter(n => !n.contact).forEach(n => {
      const contact = storeContacts.findByPServed(n.from)
      if (contact) {
        n.description = contact.name
        n.contact = contact
      }
    })
  })

  onNotification((event: NotificationIncomingEvent) => {
    handleNotificationMessage({
      item: event,
      eventId: event.eventId,
      timestamp: event.timestamp,
      type: 9,
      rank: event.rank,
      status: MessageStatusType.ACCEPTED,
    })
  })

  onDeleteMessage((event: MessageDeletedByOtherEvent) => {
    removeNotificationByTargetEventId(event.targetEventId)
  })

  onConferenceEdited((event: AnyConferenceChangedByOtherEvent) => {
    if (event.type === 'ConferenceRemoved') {
      notifications.value.filter((notifications) => {
        notifications.unread = false
      })
    }
  })

  return {
    notifications,
    unreadCount,
    addNotification,
    updateUnread,
    removeNotificationByTargetEventId,
    markReactionNotificationAsRead,
  }
})
